// Blogs.jsx
import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import axiosInstance from "../../utils/axios/axiosConfig"
import { toast } from "sonner"
import { Loading, DeleteModal } from "../../components"
import { useAccessChecker } from "../../utils/Functions/Authorization"
import { motion, AnimatePresence } from "framer-motion"
import {
  MagnifyingGlass,
  Plus,
  PencilSimple,
  Trash,
  CalendarBlank,
  Image as ImageIcon,
} from "phosphor-react"

function Blogs() {
  const { checkAccess } = useAccessChecker()

  const [blogId, setBlogId] = useState(null)
  const [searchValue, setSearchValue] = useState("")
  const [loading, setLoading] = useState(false)
  const [blogs, setBlogs] = useState([])
  const [done, setDone] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  useEffect(() => {
    getBlogs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blogId, done, currentPage, searchValue])

  const getBlogs = async () => {
    try {
      setLoading(true)
      const res = await axiosInstance.post(`/blogs/search?page=${currentPage}&per_page=10`, {
        searchValue,
      })
      const data = res.data
      if (data) {
        setBlogs(data.data || [])
        setCurrentPage(data.current_page || 1)
        setTotalPages(data.last_page || 1)
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to fetch posts")
    } finally {
      setLoading(false)
    }
  }

  const handleNext = () => currentPage < totalPages && setCurrentPage((p) => p + 1)
  const handlePrevious = () => currentPage > 1 && setCurrentPage((p) => p - 1)

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col w-full h-full px-6 py-5 bg-neutral-50/80"
    >
      {loading && <Loading />}

      {/* Header */}
      <div className="flex lg:justify-between gap-3 lg:items-center flex-col lg:flex-row mb-6">
        <div className="flex flex-col items-start gap-1">
          <h1 className="text-xl font-semibold text-neutral-900 dark:text-white">Blogs</h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">Manage posts here.</p>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center gap-3 w-full lg:w-auto">
          {/* Search */}
          <div className="relative w-full lg:w-auto">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => {
                setCurrentPage(1)
                setSearchValue(e.target.value)
              }}
              placeholder="Search posts..."
              className="pl-10 pr-4 py-2 lg:w-72 w-full text-sm border border-neutral-200 dark:border-neutral-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-900 dark:text-white"
            />
          </div>

          {/* New */}
          {checkAccess("blogs", "create") && (
            <Link
              to="/blogs/new"
              className="inline-flex justify-center items-center px-4 py-2 text-sm font-medium text-white bg-[#2B2B35] rounded-xl hover:bg-cyan-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="mr-2" size={18} />
              New post
            </Link>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <AnimatePresence>
          {blogs.map((blog) => {
            const date = blog?.created_at ? new Date(blog.created_at) : null

            return (
              <motion.div
                key={blog.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.2 }}
                className="group overflow-hidden rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                {/* Image */}
                <div className="relative h-40 bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
                  {blog.image ? (
                    <img
                      src={blog.image}
                      alt={blog.title || "Post image"}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon size={28} className="text-neutral-400" />
                    </div>
                  )}

                  {/* subtle overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-black/0 via-black/0 to-black/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>

                {/* Body */}
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2 text-xs text-neutral-500 dark:text-neutral-400">
                    <span className="inline-flex items-center gap-1">
                      <CalendarBlank size={14} />
                      {date
                        ? date.toLocaleDateString("en-US", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        : "—"}
                    </span>
                  </div>

                  <h2 className="mt-2 text-sm text-start font-semibold text-neutral-900 dark:text-white line-clamp-2 group-hover:text-cyan-600 transition-colors">
                    {blog.title || "Untitled"}
                  </h2>

                  <p className="mt-2 text-xs text-start text-neutral-600 dark:text-neutral-400 line-clamp-2">
                    {blog.description || "—"}
                  </p>

                  {/* Actions */}
                  <div className="mt-4 pt-3 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between">
                    {checkAccess("blogs", "update") ? (
                      <Link
                        to={`/blogs/update/${blog.id}`}
                        className="inline-flex items-center gap-2 text-sm font-medium text-neutral-800 dark:text-neutral-200 hover:text-cyan-600"
                      >
                        <PencilSimple size={16} />
                        Edit
                      </Link>
                    ) : (
                      <span />
                    )}

                    {checkAccess("blogs", "delete") && (
                      <button
                        type="button"
                        onClick={() => {
                          setBlogId(blog.id)
                          setDeleteModal(true)
                        }}
                        className="inline-flex items-center gap-2 text-sm font-medium text-red-600 hover:text-red-700"
                      >
                        <Trash size={16} />
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            )
          })}
        </AnimatePresence>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Page <span className="font-medium text-neutral-900 dark:text-white">{currentPage}</span> of{" "}
            <span className="font-medium text-neutral-900 dark:text-white">{totalPages}</span>
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={handlePrevious}
              disabled={currentPage === 1}
              className="px-4 py-2 text-sm font-medium rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={handleNext}
              disabled={currentPage === totalPages}
              className="px-4 py-2 text-sm font-medium rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <DeleteModal
        setDeleteId={setBlogId}
        open={deleteModal}
        setOpen={setDeleteModal}
        DeleteId={blogId}
        Model="blogs"
        setDone={setDone}
        setLoding={setLoading}
        done={done}
      />
    </motion.div>
  )
}

export default Blogs
